import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { SqlEditor } from '@/components/ui/SqlEditor';
import { Eye } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiFetch, getStoredToken } from '@/api';
import { useAuth } from '@/context/useAuth';
import { DataPreviewModal } from '@/components/common/DataPreviewModal';
import { Button } from '@/components/ui/button';

interface SourceNodeConfigProps {
    node: any;
    onChangeData: (key: string | Record<string, any>, value?: any) => void;
}

export const SourceNodeConfig = ({ node, onChangeData }: SourceNodeConfigProps) => {
    const { token } = useAuth();
    const data = node.data || {};
    const [datasets, setDatasets] = useState<any[]>([]);
    const [datasources, setDatasources] = useState<any[]>([]);
    const [tables, setTables] = useState<any[]>([]);
    const [loadingDatasets, setLoadingDatasets] = useState(false);
    const [loadingDatasources, setLoadingDatasources] = useState(false);
    const [loadingTables, setLoadingTables] = useState(false);

    // Data Preview State
    const [previewModal, setPreviewModal] = useState({ isOpen: false, dataSourceId: '', objectName: '', query: '' });



    // Fetch Data Models on mount
    useEffect(() => {
        if (!token) return;
        const fetchDataModels = async () => {
            setLoadingDatasets(true);
            try {
                // Fetch Data Models (Semantic Models)
                const data = await apiGet('/datamodels', token);
                if (data) {
                    setDatasets(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                console.error('Failed to fetch data models', e);
            } finally {
                setLoadingDatasets(false);
            }
        };
        fetchDataModels();
    }, [token]);

    // Fetch datasources on mount
    useEffect(() => {
        if (!token) return;
        const fetchDatasources = async () => {
            setLoadingDatasources(true);
            try {
                const data = await apiGet('/datasources', token);
                if (data) {
                    setDatasources(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                console.error('Failed to fetch datasources', e);
            } finally {
                setLoadingDatasources(false);
            }
        };
        fetchDatasources();
    }, [token]);

    // Fetch tables when datasource is selected in 'reuse' mode
    // connectionMode defaults to undefined which means 'reuse' (Data Source tab)
    useEffect(() => {
        const isReuseMode = !data.connectionMode || data.connectionMode === 'reuse';
        if (!data.dataSourceId || !isReuseMode || !token) return;

        const fetchTables = async () => {
            setLoadingTables(true);
            try {
                const structure = await apiGet(`/datasources/${data.dataSourceId}/structure`, token);
                if (structure) {
                    setTables(structure.tables || []);
                }
            } catch (e) {
                console.error('[SourceNodeConfig] Exception fetching tables:', e);
                setTables([]);
            } finally {
                setLoadingTables(false);
            }
        };
        fetchTables();
    }, [data.dataSourceId, data.connectionMode, token]);

    const handleConnectionModeChange = (mode: string) => {
        onChangeData({ connectionMode: mode });
    };

    // Helper to get selected model tables
    const getSelectedModelTables = () => {
        // Support both old datasetId and new dataModelId for transition
        const id = data.dataModelId || data.datasetId;
        const model = datasets.find(d => d.id === id);
        return model?.tables || [];
    };



    return (
        <div className="space-y-6">
            {/* Connection Mode Toggle */}
            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Source Configuration</label>
                <div className="flex p-1 bg-gray-100 rounded-lg gap-1">
                    {['reuse', 'dataset'].map(mode => (
                        <button
                            key={mode}
                            type="button"
                            className={`flex-1 text-[10px] py-1.5 rounded-md font-medium capitalize transition-all ${(data.connectionMode === mode || (!data.connectionMode && mode === (data.datasetId ? 'dataset' : 'reuse')))
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => handleConnectionModeChange(mode)}
                        >
                            {mode === 'dataset' ? 'Data Models' : 'Data Source'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Data Model Mode */}
            {(!data.connectionMode || data.connectionMode === 'dataset') && (
                <div className={`space-y-4 ${(!data.connectionMode && !data.datasetId) ? 'hidden' : ''}`}>
                    {/* Data Model Selector */}
                    <div className="space-y-2">
                        <label className="text-[11px] font-medium text-gray-500">Select Data Model</label>
                        <Select
                            value={data.dataModelId || data.datasetId || ''}
                            onValueChange={(v) => {
                                const model = datasets.find(d => d.id === v);
                                const updates: any = {
                                    dataModelId: v,
                                    datasetId: v // Keep for backward compatibility
                                };
                                if (model) {
                                    updates.label = model.name;
                                    updates.table = '';
                                    updates.tables = [];
                                    updates.columns = [];
                                    updates.outputSchema = [];
                                }
                                onChangeData(updates);
                            }}
                        >
                            <SelectTrigger className="w-full text-sm px-3 py-2">
                                <SelectValue placeholder={loadingDatasets ? "Loading..." : "Select a Data Model..."} />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                {datasets.map((ds) => (
                                    <SelectItem key={ds.id} value={ds.id}>{ds.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Table Selector OR Custom SQL Toggle - if model selected */}
                    {data.dataModelId && (
                        <div className="space-y-4">
                            {/* Query Mode Toggle */}
                            <div className="space-y-2">
                                <label className="text-[11px] font-medium text-gray-500">Query Mode</label>
                                <div className="flex p-1 bg-gray-100 rounded-lg gap-1">
                                    <button
                                        type="button"
                                        className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${!data.isDatasetSql
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        onClick={() => onChangeData({ isDatasetSql: false })}
                                    >
                                        Auto Join Tables
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${data.isDatasetSql
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        onClick={() => onChangeData({ isDatasetSql: true })}
                                    >
                                        Custom SQL
                                    </button>
                                </div>
                            </div>

                            {data.isDatasetSql ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between pointer-events-none">
                                        <label className="text-[11px] font-medium text-gray-500">Custom SQL Query</label>
                                    </div>
                                    <SqlEditor
                                        value={data.datasetSql || ''}
                                        onChange={(val) => onChangeData({ datasetSql: val })}
                                        className="border-gray-200"
                                    />
                                    <p className="text-[10px] text-gray-400">
                                        Write raw SQL. Tables are registered as views. <br />
                                        Example: <code className="bg-gray-100 px-1 rounded">SELECT a.*, c.CalendarYear FROM DimAdjustment a JOIN DimCalendar c ON a.DateKey = c.DateKey</code>
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2 border border-gray-100 rounded-lg p-3 bg-white">
                                    <label className="text-[11px] font-medium text-gray-500 block mb-1">Select Tables</label>
                                    <p className="text-[10px] text-gray-500 mb-2">Select one or more tables. All columns from selected tables will be available for downstream ML nodes.</p>
                                    <label className="flex items-center space-x-2 py-1.5 px-2 mb-2 rounded hover:bg-gray-100 cursor-pointer border border-gray-200">
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            checked={getSelectedModelTables().length > 0 && Array.isArray(data.tables) && data.tables.length === getSelectedModelTables().length}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                const modelTables = getSelectedModelTables();
                                                if (isChecked && modelTables.length > 0) {
                                                    const next = modelTables.map((t: any) => t.name);
                                                    const updates: any = { tables: next, table: next[0] || '' };
                                                    const seen = new Set<string>();
                                                    const allCols: { name: string; type: string }[] = [];
                                                    next.forEach((tblName: string) => {
                                                        const tbl = modelTables.find((m: any) => m.name === tblName);
                                                        if (tbl && Array.isArray(tbl.columns)) {
                                                            tbl.columns.forEach((c: any) => {
                                                                const baseName = c.name;
                                                                const colName = seen.has(baseName) ? `${tblName}_${baseName}` : baseName;
                                                                seen.add(baseName);
                                                                allCols.push({ name: colName, type: c.data_type || 'string' });
                                                            });
                                                        }
                                                    });
                                                    updates.columns = allCols;
                                                    updates.outputSchema = allCols;
                                                    onChangeData(updates);
                                                    toast.success(`Selected all ${next.length} tables`);
                                                } else {
                                                    onChangeData({ tables: [], table: '', columns: [], outputSchema: [] });
                                                    toast.success('Cleared tables');
                                                }
                                            }}
                                        />
                                        <span className="text-sm font-semibold text-gray-700">Select All</span>
                                    </label>
                                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                                        {getSelectedModelTables().map((t: any) => {
                                            const selectedTables = Array.isArray(data.tables) ? data.tables : (data.table ? [data.table] : []);
                                            const isChecked = selectedTables.includes(t.name);
                                            return (
                                                <div
                                                    key={t.name}
                                                    className="flex items-center space-x-2 py-1.5 px-2 rounded hover:bg-gray-100 cursor-pointer"
                                                    onClick={() => {
                                                        const current = Array.isArray(data.tables) ? [...data.tables] : (data.table ? [data.table] : []);
                                                        const next = isChecked
                                                            ? current.filter((x: string) => x !== t.name)
                                                            : [...current, t.name];
                                                        const updates: any = { tables: next, table: next[0] || '' };
                                                        if (next.length > 0) {
                                                            const modelTables = getSelectedModelTables();
                                                            const seen = new Set<string>();
                                                            const allCols: { name: string; type: string }[] = [];
                                                            next.forEach((tblName: string) => {
                                                                const tbl = modelTables.find((m: any) => m.name === tblName);
                                                                if (tbl && Array.isArray(tbl.columns)) {
                                                                    tbl.columns.forEach((c: any) => {
                                                                        const baseName = c.name;
                                                                        const dataType = c.data_type || 'string';
                                                                        let colName = baseName;
                                                                        if (seen.has(baseName)) colName = `${tblName}_${baseName}`;
                                                                        else seen.add(baseName);
                                                                        allCols.push({ name: colName, type: dataType });
                                                                    });
                                                                }
                                                            });
                                                            updates.columns = allCols;
                                                            updates.outputSchema = allCols;
                                                        } else {
                                                            updates.columns = [];
                                                            updates.outputSchema = [];
                                                        }
                                                        onChangeData(updates);
                                                        toast.success(next.length ? `Loaded ${updates.outputSchema?.length || 0} columns from ${next.length} table(s)` : 'Cleared tables');
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                        checked={isChecked}
                                                        onChange={() => { }}
                                                    />
                                                    <span className="text-sm text-gray-700">{t.name}</span>
                                                    {t.columns && <span className="text-[10px] text-gray-400">({t.columns.length} cols)</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {(Array.isArray(data.tables) ? data.tables : data.table ? [data.table] : []).length > 0 && (
                                        <p className="text-[10px] text-indigo-600">
                                            {Array.isArray(data.outputSchema) ? data.outputSchema.length : 0} columns available for ML nodes
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Reuse (Connection) Mode */}
            {(!data.connectionMode || data.connectionMode === 'reuse') && (
                <div className={`space-y-4 ${data.connectionMode === 'dataset' ? 'hidden' : ''}`}>
                            {/* Connection Selector */}
                            <div className="space-y-2">
                                <label className="text-[11px] font-medium text-gray-500">Select Connection</label>
                                <Select
                                    value={data.dataSourceId || ''}
                                    onValueChange={(v) => {
                                        onChangeData({ dataSourceId: v });
                                        // Eagerly fetch tables immediately on selection so they
                                        // appear without needing a tab switch / re-render cycle
                                        setTables([]);
                                        setLoadingTables(true);
                                        apiGet(`/datasources/${v}/structure`)
                                            .then(s => setTables(s?.tables || []))
                                            .catch(() => setTables([]))
                                            .finally(() => setLoadingTables(false));
                                    }}
                                >
                                    <SelectTrigger className="w-full text-sm px-3 py-2">
                                        <SelectValue placeholder={loadingDatasources ? "Loading Connections..." : "Select a Connection..."} />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                        {datasources.map((conn) => (
                                            <SelectItem key={conn.id} value={conn.id}>
                                                {conn.name} ({conn.type})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Query Mode Toggle: Table vs Custom SQL */}
                            <div className="space-y-2">
                                <label className="text-[11px] font-medium text-gray-500">Query Mode</label>
                                <div className="flex p-1 bg-gray-100 rounded-lg gap-1">
                                    <button
                                        type="button"
                                        className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${!data.isCustomSql
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        onClick={() => onChangeData({ isCustomSql: false })}
                                    >
                                        Select Table
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${data.isCustomSql
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                        onClick={() => onChangeData({ isCustomSql: true })}
                                    >
                                        Custom SQL
                                    </button>
                                </div>
                            </div>

                            {/* Custom SQL Editor OR Table Selector */}
                            {data.isCustomSql ? (
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <label className="text-[11px] font-medium text-gray-500">SQL Query</label>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 gap-1 text-[10px]"
                                            onClick={() => setPreviewModal({ isOpen: true, dataSourceId: data.dataSourceId, objectName: '', query: data.customSql })}
                                            disabled={!data.customSql}
                                        >
                                            <Eye className="h-3 w-3" /> Preview
                                        </Button>
                                    </div>
                                    <SqlEditor
                                        value={data.customSql || ''}
                                        onChange={(val) => onChangeData({ customSql: val })}
                                        className="border-gray-200"
                                    />
                                    <p className="text-[10px] text-gray-400">Write your own query instead of selecting a table.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <label className="text-[11px] font-medium text-gray-500">Table</label>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 gap-1 text-[10px]"
                                            onClick={() => setPreviewModal({ isOpen: true, dataSourceId: data.dataSourceId, objectName: data.table, query: '' })}
                                            disabled={!data.table}
                                        >
                                            <Eye className="h-3 w-3" /> Preview
                                        </Button>
                                    </div>
                                    <Select
                                        value={data.table || ''}
                                        onValueChange={async (v) => {
                                            onChangeData({ table: v });
                                            // Auto-fetch columns for this table so downstream nodes can resolve schema
                                            if (data.dataSourceId && v) {
                                                try {
                                                    const schema = await apiGet(`/datasources/${data.dataSourceId}/schema`);
                                                    if (Array.isArray(schema)) {
                                                        const table = schema.find((t: any) => t.name === v);
                                                        if (table && Array.isArray(table.columns)) {
                                                            onChangeData({ columns: table.columns, outputSchema: table.columns });
                                                            toast.success(`Loaded ${table.columns.length} columns from ${v}`);
                                                        }
                                                    }
                                                } catch (e) { console.error('Failed to auto-fetch columns', e); }
                                            }
                                        }}
                                        disabled={!data.dataSourceId || loadingTables}
                                    >
                                        <SelectTrigger className="w-full text-sm px-3 py-2">
                                            <SelectValue placeholder={loadingTables ? "Loading tables..." : (tables.length > 0 ? "Select Table..." : "No tables found")} />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                            {tables.map((t) => (
                                                <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                </div>
            )}



            {/* Data Preview Modal */}
            <DataPreviewModal
                isOpen={previewModal.isOpen}
                onClose={() => setPreviewModal({ ...previewModal, isOpen: false })}
                dataSourceId={previewModal.dataSourceId || ''}
                objectName={previewModal.objectName || ''}
                query={previewModal.query || ''}
            />
        </div>
    );
};

export default SourceNodeConfig;
